import PropTypes from 'prop-types';
import { Box, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';

import EmailDirectionDropdown from './EmailDirectionDropdown';
import MicrosoftFolderDropdown from './MicrosoftFolderDropdown';
import GmailLabelDropdown from './GmailLabelDropdown';

const BasicSettings = ({
	tenant,
	tenantLevel,

	emailBodySync,
	setEmailBodySync,

	syncAttachments,
	setSyncAttachments,

	emailDirection,
	setEmailDirection,

	hasMicrosoftProvider = false,
	microsoftFolders = [],
	selectedMicrosoftFolder = '',
	handleMicrosoftFolderChange,
	microsoftFolderLoading = false,
	microsoftFolderError = null,

	hasGoogleProvider = false,
	gmailLabels = [],
	selectedGmailLabel = '',
	handleGmailLabelChange,
	gmailLabelLoading = false,
	gmailLabelError = null,

	isRunning = false,
}) => {
	const theme = useTheme();

	return (
		<Stack spacing={2}>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 600,
					fontSize: '0.9rem',
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
				}}
				gutterBottom
			>
				Basic Settings
			</Typography>

			{/* Include Email Body in CRM Sync */}
			<Paper
				variant='outlined'
				sx={{
					px: 3,
					py: 2,
					borderRadius: 0.8,
					backgroundColor: theme.palette.background.default,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
					}}
				>
					<Box sx={{ flex: 1, mr: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Typography
								variant='subtitle1'
								sx={{
									fontWeight: 600,
									mb: 0.1,
									fontSize: '0.9rem',
								}}
							>
								Include Email Body in CRM Sync
							</Typography>

							{!tenantLevel && !tenant.email_body_sync && (
								<Tooltip
									title='This setting is controlled by your tenant admin'
									placement='top'
									arrow
									slotProps={{
										tooltip: {
											sx: {
												borderRadius: '5px',
											},
										},
									}}
								>
									<InfoOutlined
										sx={{
											fontSize: '1rem',
											color: 'text.secondary',
											cursor: 'help',
											mb: 0.3,
										}}
									/>
								</Tooltip>
							)}
						</Box>

						<Typography
							variant='body2'
							color='text.secondary'
							sx={{ fontSize: '0.8rem' }}
						>
							Sync the full email content to your CRM records
						</Typography>
					</Box>

					<Switch
						checked={emailBodySync}
						onChange={(e) => setEmailBodySync(e.target.checked)}
						disabled={!tenantLevel && !tenant.email_body_sync}
						slotProps={{
							input: {
								'data-testid': 'email-body-sync-switch',
							},
						}}
					/>
				</Box>
			</Paper>

			{/* Sync Email Attachments */}
			<Paper
				variant='outlined'
				sx={{
					px: 3,
					py: 2,
					borderRadius: 0.8,
					backgroundColor: theme.palette.background.default,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
					}}
				>
					<Box sx={{ flex: 1, mr: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Typography
								variant='subtitle1'
								sx={{
									fontWeight: 600,
									mb: 0.1,
									fontSize: '0.9rem',
								}}
							>
								Include Email Attachments in CRM Sync
							</Typography>

							{!tenantLevel && (
								<Tooltip
									title='This setting is controlled by your tenant admin'
									placement='top'
									arrow
									slotProps={{
										tooltip: {
											sx: {
												borderRadius: '5px',
											},
										},
									}}
								>
									<InfoOutlined
										sx={{
											fontSize: '1rem',
											color: 'text.secondary',
											cursor: 'help',
											mb: 0.3,
										}}
									/>
								</Tooltip>
							)}
						</Box>

						<Typography
							variant='body2'
							color='text.secondary'
							sx={{ fontSize: '0.8rem' }}
						>
							Sync email attachments to your CRM records
						</Typography>
					</Box>

					<Switch
						checked={syncAttachments}
						onChange={(e) => setSyncAttachments(e.target.checked)}
						disabled={!tenantLevel}
						slotProps={{
							input: {
								'data-testid': 'sync-attachments-switch',
							},
						}}
					/>
				</Box>
			</Paper>

			{/* Email Ingestion Direction */}
			<EmailDirectionDropdown
				emailDirection={emailDirection}
				onEmailDirectionChange={setEmailDirection}
				tenantLevel={tenantLevel}
			/>

			{/* User-level Microsoft setting */}
			{hasMicrosoftProvider && (
				<MicrosoftFolderDropdown
					folders={microsoftFolders}
					selectedFolder={selectedMicrosoftFolder}
					onFolderChange={handleMicrosoftFolderChange}
					isLoading={microsoftFolderLoading}
					error={microsoftFolderError}
					disabled={isRunning}
					emailDirection={emailDirection}
				/>
			)}

			{/* User-level Gmail setting */}
			{hasGoogleProvider && (
				<GmailLabelDropdown
					labels={gmailLabels}
					selectedLabel={selectedGmailLabel}
					onLabelChange={handleGmailLabelChange}
					isLoading={gmailLabelLoading}
					error={gmailLabelError}
					disabled={isRunning}
					emailDirection={emailDirection}
				/>
			)}
		</Stack>
	);
};

BasicSettings.propTypes = {
	tenant: PropTypes.shape({
		email_body_sync: PropTypes.bool.isRequired,
	}).isRequired,
	tenantLevel: PropTypes.bool.isRequired,
	emailBodySync: PropTypes.bool.isRequired,
	setEmailBodySync: PropTypes.func.isRequired,
	syncAttachments: PropTypes.bool.isRequired,
	setSyncAttachments: PropTypes.func.isRequired,
	emailDirection: PropTypes.oneOf(['received', 'sent', 'received_and_sent'])
		.isRequired,
	setEmailDirection: PropTypes.func.isRequired,
	hasMicrosoftProvider: PropTypes.bool,
	microsoftFolders: PropTypes.arrayOf(PropTypes.object),
	selectedMicrosoftFolder: PropTypes.string,
	handleMicrosoftFolderChange: PropTypes.func,
	microsoftFolderLoading: PropTypes.bool,
	microsoftFolderError: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
	]),
	hasGoogleProvider: PropTypes.bool,
	gmailLabels: PropTypes.arrayOf(PropTypes.object),
	selectedGmailLabel: PropTypes.string,
	handleGmailLabelChange: PropTypes.func,
	gmailLabelLoading: PropTypes.bool,
	gmailLabelError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

	isRunning: PropTypes.bool,
};

export default BasicSettings;
